import { cva } from "class-variance-authority";
import * as React from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "~/components/ui/tooltip";
import { cn } from "~/lib/utils";
import type { Definition } from "~/server/db/lemmas/schema";

const badgePosVariants = cva(
  "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        noun: "border-transparent bg-blue-100 text-blue-800",
        pronoun: "border-transparent bg-blue-200 text-blue-900",
        verb: "border-transparent bg-green-100 text-green-800",
        verbSpecial: "border-transparent bg-green-200 text-green-900",
        adj: "border-transparent bg-purple-100 text-purple-800",
        adv: "border-transparent bg-amber-100 text-amber-800",
        auxiliary: "border-transparent bg-rose-100 text-rose-800",
        counter: "border-transparent bg-indigo-100 text-indigo-800",
        expression: "border-transparent bg-orange-100 text-orange-800",
        affix: "border-transparent bg-slate-100 text-slate-800",
        default: "border-transparent bg-gray-100 text-gray-800",
        particle: "border-transparent bg-rose-100 text-rose-800",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

interface BadgePosProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "children"> {
  pos: Definition["pos"];
  showTooltip?: boolean;
}

function formatPos(pos: string | null | undefined): string {
  if (!pos) return "";
  return pos
    .replace(/[\[\]]/g, "")
    .split(",")
    .join(", ");
}

function getVariantFromPos(pos: string | null | undefined) {
  if (!pos) return "default";

  const firstPos = pos.split(",")[0]?.trim().toLowerCase();
  if (!firstPos) return "default";

  const cleanPos = firstPos.replace(/[\[\]]/g, "");

  if (cleanPos.startsWith("v")) {
    if (cleanPos === "vi" || cleanPos === "vt") return "verbSpecial";
    if (
      cleanPos.startsWith("v5") ||
      cleanPos.startsWith("v1") ||
      cleanPos === "vs" ||
      cleanPos === "vk"
    )
      return "verb";
    return "verb";
  }

  if (cleanPos.startsWith("n")) return "noun";
  if (cleanPos === "pn") return "pronoun";
  if (cleanPos.startsWith("adj")) return "adj";
  if (cleanPos.startsWith("adv")) return "adv";
  if (cleanPos.startsWith("aux")) return "auxiliary";
  if (cleanPos === "ctr" || cleanPos === "num") return "counter";
  if (["exp", "conj", "int"].includes(cleanPos)) return "expression";
  if (cleanPos.startsWith("pref") || cleanPos.startsWith("suf")) return "affix";
  if (cleanPos === "prt") return "particle";
  if (cleanPos.startsWith("cop")) return "auxiliary";

  return "default";
}

// Define the type for our POS name structure
type PosName = {
  en: string;
  ja: string;
  ruby: string;
};

// Define the type for our mapping
const posFullNames: Record<string, PosName> = {
  // Nouns
  n: {
    en: "Noun",
    ja: "一般名詞",
    ruby: "いっぱんめいし",
  },
  "n-adv": {
    en: "Adverbial Noun",
    ja: "副詞的名詞",
    ruby: "ふくしてきめいし",
  },
  "n-t": {
    en: "Temporal Noun",
    ja: "時名詞",
    ruby: "じめいし",
  },

  // Pronouns
  pn: {
    en: "Pronoun",
    ja: "代名詞",
    ruby: "だいめいし",
  },

  // Verbs
  v1: {
    en: "Ichidan Verb",
    ja: "一段動詞",
    ruby: "いちだんどうし",
  },
  v5: {
    en: "Godan Verb",
    ja: "五段動詞",
    ruby: "ごだんどうし",
  },
  vi: {
    en: "Intransitive Verb",
    ja: "自動詞",
    ruby: "じどうし",
  },
  vt: {
    en: "Transitive Verb",
    ja: "他動詞",
    ruby: "たどうし",
  },
  vs: {
    en: "Suru Verb",
    ja: "する動詞",
    ruby: "するどうし",
  },

  // Adjectives
  adj: {
    en: "Adjective",
    ja: "形容詞",
    ruby: "けいようし",
  },
  "adj-i": {
    en: "I-Adjective",
    ja: "形容詞",
    ruby: "けいようし",
  },
  "adj-ix": {
    en: "I-Adjective (irregular)",
    ja: "不規則な形容詞",
    ruby: "ふきそくなけいようし",
  },
  "adj-na": {
    en: "Na-Adjective",
    ja: "形容動詞",
    ruby: "けいようどうし",
  },
  "adj-no": {
    en: "No-Adjective",
    ja: "の形容詞",
    ruby: "のけいようし",
  },
  "adj-pn": {
    en: "Pre-noun Adjective",
    ja: "連体詞",
    ruby: "れんたいし",
  },
  "adj-t": {
    en: "Taru Adjective",
    ja: "たる形容詞",
    ruby: "たるけいようし",
  },
  "adj-f": {
    en: "Pre-noun Adjectival",
    ja: "連体詞的",
    ruby: "れんたいしてき",
  },

  // Adverbs
  adv: {
    en: "Adverb",
    ja: "副詞",
    ruby: "ふくし",
  },
  "adv-to": {
    en: "To-Adverb",
    ja: "と副詞",
    ruby: "とふくし",
  },

  // Particles & Auxiliaries
  aux: {
    en: "Auxiliary",
    ja: "助動詞",
    ruby: "じょどうし",
  },
  prt: {
    en: "Particle",
    ja: "助詞",
    ruby: "じょし",
  },

  // Counters & Numbers
  ctr: {
    en: "Counter",
    ja: "助数詞",
    ruby: "じょすうし",
  },
  num: {
    en: "Numeric",
    ja: "数詞",
    ruby: "すうし",
  },

  // Expressions & Conjunctions
  exp: {
    en: "Expression",
    ja: "表現",
    ruby: "ひょうげん",
  },
  conj: {
    en: "Conjunction",
    ja: "接続詞",
    ruby: "せつぞくし",
  },
  int: {
    en: "Interjection",
    ja: "感動詞",
    ruby: "かんどうし",
  },

  // Affixes
  pref: {
    en: "Prefix",
    ja: "接頭辞",
    ruby: "せっとうじ",
  },
  suf: {
    en: "Suffix",
    ja: "接尾辞",
    ruby: "せつびじ",
  },

  // Copulas
  cop: {
    en: "Copula",
    ja: "繋辞",
    ruby: "けいじ",
  },
  "cop-da": {
    en: "Copula (だ)",
    ja: "だ繋辞",
    ruby: "だけいじ",
  },

  // More specific verb types
  v5r: {
    en: "Godan Verb (る)",
    ja: "五段動詞-ラ行",
    ruby: "ごだんどうし-らぎょう",
  },
  v5k: {
    en: "Godan Verb (く)",
    ja: "五段動詞-カ行",
    ruby: "ごだんどうし-かぎょう",
  },
  v5g: {
    en: "Godan Verb (ぐ)",
    ja: "五段動詞-ガ行",
    ruby: "ごだんどうし-がぎょう",
  },
  v5s: {
    en: "Godan Verb (す)",
    ja: "五段動詞-サ行",
    ruby: "ごだんどうし-さぎょう",
  },
  v5t: {
    en: "Godan Verb (つ)",
    ja: "五段動詞-タ行",
    ruby: "ごだんどうし-たぎょう",
  },
  v5n: {
    en: "Godan Verb (ぬ)",
    ja: "五段動詞-ナ行",
    ruby: "ごだんどうし-なぎょう",
  },
  v5b: {
    en: "Godan Verb (ぶ)",
    ja: "五段動詞-バ行",
    ruby: "ごだんどうし-ばぎょう",
  },
  v5m: {
    en: "Godan Verb (む)",
    ja: "五段動詞-マ行",
    ruby: "ごだんどうし-まぎょう",
  },
  v5u: {
    en: "Godan Verb (う)",
    ja: "五段動詞-ワ行",
    ruby: "ごだんどうし-わぎょう",
  },
  vk: {
    en: "Kuru Verb",
    ja: "来る動詞",
    ruby: "くるどうし",
  },
} as const;

function getFullPosName(pos: string | null | undefined): PosName[] {
  if (!pos) return [{ en: "Unknown", ja: "不明", ruby: "ふめい" }];

  // Split multiple POS tags and map each one
  const posTags = pos
    .replace(/[\[\]]/g, "")
    .split(",")
    .map((tag) => tag.trim().toLowerCase());

  return posTags.map(
    (tag) => posFullNames[tag] ?? { en: tag, ja: "", ruby: "" },
  );
}

export function BadgePos({
  pos,
  className,
  showTooltip = false,
  ...props
}: BadgePosProps) {
  const fullNames = getFullPosName(pos);

  if (!showTooltip) {
    return (
      <div
        className={cn(
          badgePosVariants({ variant: getVariantFromPos(pos) }),
          className,
        )}
        {...props}
      >
        {formatPos(pos)}
      </div>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          className={cn(
            badgePosVariants({ variant: getVariantFromPos(pos) }),
            className,
          )}
          {...props}
        >
          {formatPos(pos)}
        </div>
      </TooltipTrigger>
      <TooltipContent
        side="left"
        sideOffset={4}
        className="bg-primary text-primary-foreground"
      >
        <div className="flex flex-col gap-1">
          {fullNames.map((name, i) => (
            <div key={i} className="flex flex-col">
              <div>{name.en}</div>
              <ruby>
                {name.ja}
                <rp>(</rp>
                <rt>{name.ruby}</rt>
                <rp>)</rp>
              </ruby>
            </div>
          ))}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}
